import { authService } from '~/services/auth-service';
import type { AuthUser, PbAccessToken } from '~/types/auth/auth';

export const useAuthStore = defineStore(
    'auth',
    () => {
        const config = useRuntimeConfig();

        // Environment-scoped auth cookie names (suffixed on staging/dev so they
        // never collide with production's cookies at .privacybee.com).
        const { tokenCookieName, refreshCookieName } = useAuthCookieNames();

        const user = ref(null as AuthUser | null);

        // Use 12-hour expiration for access token
        // NOTE: On client-side only apps, useCookie with domain option doesn't read existing cookies properly
        // So we read without options first to get the existing value
        const tokenCookie = useCookie<string | null>(tokenCookieName, {
            default: () => null,
        });

        const refreshTokenCookie = useCookie<string | null>(refreshCookieName, {
            default: () => null,
        });

        const token = computed(() => tokenCookie.value);
        const isRefreshing = ref(false);
        let refreshPromise: Promise<boolean> | null = null;
        
        // Request queue for handling requests during token refresh
        interface QueuedRequest {
            resolve: (token: string | null) => void;
            reject: (error: any) => void;
        }
        const requestQueue: QueuedRequest[] = [];

        const loggedIn = computed(() => !!token.value || !!user.value);

        // Helper to read cookie directly from document.cookie (fallback when useCookie hasn't read it yet)
        function readCookieDirectly(name: string): string | null {
            if (import.meta.server) return null;
            
            const cookies = document.cookie.split(';');
            const cookie = cookies.find(c => c.trim().startsWith(`${name}=`));
            if (cookie) {
                return decodeURIComponent(cookie.split('=')[1]?.trim() || '');
            }
            return null;
        }

        // Helper to check if current route is public
        function isPublicRoute(): boolean {
            if (import.meta.server) return false;
            
            try {
                const router = useRouter();
                const route = router.currentRoute.value;
                return route.meta.public === true;
            } catch {
                // Fallback: check pathname against known public routes
                const publicPaths = ['/auth/login', '/auth/callback', '/auth/logout', '/brokers'];
                return publicPaths.some(path => window.location.pathname.startsWith(path));
            }
        }

        // Function to get a valid token for requests - handles refresh and queueing
        async function getValidTokenForRequest(): Promise<string | null> {
            // If we have a token from useCookie, return it
            if (token.value) {
                return token.value;
            }

            // Fallback: Try reading cookie directly from document.cookie
            // This handles race conditions where useCookie hasn't read the cookie yet after login redirect
            // Multiple requests fire simultaneously, and one might fire before useCookie has read the cookie
            const directToken = readCookieDirectly(tokenCookieName);
            if (directToken) {
                // Update tokenCookie to sync with actual cookie value (removes "Bearer " prefix if present)
                const cleanToken = directToken.startsWith('Bearer ') ? directToken : `Bearer ${directToken}`;
                tokenCookie.value = cleanToken;
                return cleanToken;
            }

            // If we're on a public route and don't have a token, return null (don't try to refresh)
            if (isPublicRoute()) {
                return null;
            }

            // If already refreshing, queue the request
            if (isRefreshing.value) {
                return new Promise((resolve, reject) => {
                    requestQueue.push({ resolve, reject });
                });
            }

            // No token, try to refresh
            const refreshed = await refreshAccessToken();
            return refreshed ? token.value : null;
        }

        // Auto-refresh token using refresh token cookie
        async function refreshAccessToken(): Promise<boolean> {
            // Get refresh token from useCookie or fallback to direct cookie read
            let refreshToken = refreshTokenCookie.value;
            if (!refreshToken) {
                refreshToken = readCookieDirectly(refreshCookieName);
                if (refreshToken) {
                    // Sync refreshTokenCookie with actual cookie value
                    refreshTokenCookie.value = refreshToken;
                }
            }
            
            if (!refreshToken) {
                // No refresh token, clear session and redirect to login
                tokenCookie.value = null;
                refreshTokenCookie.value = null;
                user.value = null;
                
                // Clear cookies from browser (not just memory)
                if (import.meta.client) {
                    clearAuthCookies();
                }
                
                // Process queue with null 
                processRequestQueue(null);
                
                // Redirect to internal login page (keeps redirect cookie in same domain)
                // But skip redirect if we're on a public route
                if (!isPublicRoute() && !window.location.pathname.startsWith('/auth/')) {
                    const redirectCookie = useCookie('pb-redirect');
                    redirectCookie.value = window.location.pathname + window.location.search;
                    await navigateTo('/auth/login');
                }
                
                return false;
            }
            
            // If already refreshing, return the existing promise
            if (refreshPromise) {
                return refreshPromise;
            }
            
            isRefreshing.value = true;
            refreshPromise = (async (): Promise<boolean> => {
                try {
                    // Use standard OAuth2 refresh flow
                    const tokenData = await $fetch<PbAccessToken>(`${config.public.pb.authHost}/oauth/token`, {
                        method: 'POST',
                        body: {
                            grant_type: 'refresh_token',
                            refresh_token: refreshToken,
                            client_id: config.public.pb.authClientId,
                        },
                    });

                    await setToken(tokenData);

                    // Process queued requests with new token
                    processRequestQueue(token.value);
                    return true;
                } catch {
                    // Refresh failed, clear tokens and redirect to login
                    tokenCookie.value = null;
                    refreshTokenCookie.value = null;
                    user.value = null;
                    
                    // Clear cookies from browser (not just memory)
                    if (import.meta.client) {
                        clearAuthCookies();
                    }
                    
                    // Process queue with null (fail all requests)
                    processRequestQueue(null);
                    
                    // Redirect to internal login page (keeps redirect cookie in same domain)
                    // But skip redirect if we're on a public route
                    if (!isPublicRoute() && !window.location.pathname.startsWith('/auth/')) {
                        const redirectCookie = useCookie('pb-redirect');
                        redirectCookie.value = window.location.pathname + window.location.search;
                        await navigateTo('/auth/login');
                    }
                    return false;
                } finally {
                    isRefreshing.value = false;
                    refreshPromise = null;
                }
            })();
            
            return refreshPromise;
        }

        // Process the request queue after refresh completes
        function processRequestQueue(newToken: string | null) {
            while (requestQueue.length > 0) {
                const queuedRequest = requestQueue.shift();
                if (queuedRequest) {
                    if (newToken) {
                        queuedRequest.resolve(newToken);
                    } else {
                        queuedRequest.reject(new Error('Token refresh failed'));
                    }
                }
            }
        }

        // Helper function to clear auth cookies from browser
        function clearAuthCookies() {
            // Only clear this environment's auth cookies. On staging/dev the names
            // are suffixed, so the unsuffixed production cookies are never touched.
            const cookiesToClear = [
                tokenCookieName,
                refreshCookieName,
                "XSRF-TOKEN",
            ];
            const domain = config.public.pb.domainHost;
            const paths = ["/"];

            cookiesToClear.forEach((cookieName) => {
                paths.forEach((path) => {
                    // Clear from parent domain
                    document.cookie = `${cookieName}=; path=${path}; domain=${domain}; expires=Thu, 01 Jan 1970 00:00:00 UTC; SameSite=Lax; Secure`;
                    // Also clear from current subdomain
                    document.cookie = `${cookieName}=; path=${path}; expires=Thu, 01 Jan 1970 00:00:00 UTC; SameSite=Lax; Secure`;
                });
            });
        }

        // Initialize authentication on app start
        async function initializeAuth(): Promise<boolean> {
            // If we already have a session token, we're good
            if (token.value) {
                return true;
            }

            // No token, try to refresh
            return await refreshAccessToken();
        }

        async function getMe() {
            try {
                // If we have a token, use it directly
                if (token.value) {
                    const userData = await authService.getMe();
                    user.value = userData;
                    return;
                }

                // No token, try to get one (will handle refresh if needed)
                const validToken = await getValidTokenForRequest();
                if (!validToken) {
                    throw new Error('No valid token available');
                }

                const userData = await authService.getMe();
                user.value = userData;
            } catch {
                tokenCookie.value = null;
                user.value = null;
            }
        }

        // Set token (from OAuth flow)
        async function setToken(tokenData: PbAccessToken) {
            // Use document.cookie to set with proper domain for cross-subdomain sharing
            const domain = config.public.pb.domainHost;
            const tokenExpires = new Date(Date.now() + (12 * 60 * 60 * 1000)); // 12 hours
            const refreshExpires = new Date(Date.now() + (30 * 24 * 60 * 60 * 1000)); // 30 days

            // Set cookies via document.cookie with explicit domain for cross-subdomain sharing
            document.cookie = `${tokenCookieName}=Bearer%20${tokenData.access_token}; path=/; domain=${domain}; expires=${tokenExpires.toUTCString()}; secure; samesite=lax`;
            document.cookie = `${refreshCookieName}=${tokenData.refresh_token}; path=/; domain=${domain}; expires=${refreshExpires.toUTCString()}; secure; samesite=lax`;
            
            // Force re-read from cookie (the useCookie composables will automatically pick up the new values)
            // We need to trigger a re-read by accessing the cookie again
            if (import.meta.client) {
                // Small delay to ensure browser has processed the cookie write
                await new Promise(resolve => setTimeout(resolve, 10));
            }
        }

        async function logout() {
            // Clear cookies and state
            user.value = null;
            localStorage.clear();

            clearAuthCookies();
            window.location.href = `${config.public.pb.authHost}/logout?redirect=${config.public.pb.appDomain}`;
        }

        // Initiate login by redirecting to auth app
        async function login(redirectPath: string, email?: string | null) {
            const redirectUrl = `${window.location.origin}${redirectPath || '/dashboard'}`;

            let authUrl = `${config.public.pb.authApp}?redirect_url=${encodeURIComponent(redirectUrl)}`;
            if (email) {
                authUrl += `&email=${encodeURIComponent(email)}`;
            }

            window.location.href = authUrl;
        }

        return {
            user,
            token: readonly(token), // Make token read-only
            loggedIn,
            getMe,
            login,
            logout,
            setToken,
            refreshAccessToken,
            getValidTokenForRequest,
            initializeAuth,
            isRefreshing: readonly(isRefreshing),
        };
    },
    {
        persist: {
            pick: ['user'],
        }
    }
);

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useAuthStore, import.meta.hot));
}